<template>
  <div>
    <h1 class="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">แดชบอร์ด</h1>
    
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      <p class="mt-4 text-gray-500">กำลังโหลดข้อมูล...</p>
    </div>

    <div v-else>
      <!-- Stats Cards -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
        <!-- Today Sales -->
        <div class="bg-linear-to-br from-green-500 to-green-600 p-4 sm:p-6 rounded-xl shadow-lg text-white">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-green-100 text-sm sm:text-base">ยอดขายวันนี้</h3>
            <svg class="w-8 h-8 text-green-200 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p class="text-2xl sm:text-3xl font-bold">฿{{ stats.todaySales || '0.00' }}</p>
          <p class="text-green-100 text-sm mt-1">{{ stats.todayOrders || 0 }} คำสั่งซื้อ</p>
        </div>

        <!-- Month Sales -->
        <div class="bg-linear-to-br from-blue-500 to-blue-600 p-4 sm:p-6 rounded-xl shadow-lg text-white">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-blue-100 text-sm sm:text-base">ยอดขายเดือนนี้</h3>
            <svg class="w-8 h-8 text-blue-200 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p class="text-2xl sm:text-3xl font-bold">฿{{ stats.monthSales || '0.00' }}</p>
          <p class="text-blue-100 text-sm mt-1">{{ stats.monthOrders || 0 }} คำสั่งซื้อ</p>
        </div>

        <!-- Pending Orders -->
        <div class="bg-linear-to-br from-amber-500 to-amber-600 p-4 sm:p-6 rounded-xl shadow-lg text-white">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-amber-100 text-sm sm:text-base">รอดำเนินการ</h3>
            <svg class="w-8 h-8 text-amber-200 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p class="text-2xl sm:text-3xl font-bold">{{ stats.pendingOrders || 0 }}</p>
          <p class="text-amber-100 text-sm mt-1">คำสั่งซื้อ</p>
        </div>

        <!-- Total Orders -->
        <div class="bg-linear-to-br from-purple-500 to-purple-600 p-4 sm:p-6 rounded-xl shadow-lg text-white">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-purple-100 text-sm sm:text-base">คำสั่งซื้อทั้งหมด</h3>
            <svg class="w-8 h-8 text-purple-200 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <p class="text-2xl sm:text-3xl font-bold">{{ stats.totalOrders || 0 }}</p>
          <p class="text-purple-100 text-sm mt-1">ยอดขายรวม ฿{{ stats.totalSales || '0.00' }}</p>
        </div>
      </div>

      <!-- Order Status Summary & Best Sellers -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
        <div class="bg-white p-4 sm:p-6 rounded-xl shadow">
          <h3 class="font-bold text-gray-700 mb-4">สถานะคำสั่งซื้อ</h3>
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-full bg-yellow-400"></span>
                <span class="text-gray-600">รอดำเนินการ</span>
              </div>
              <span class="font-bold text-gray-800">{{ stats.pendingOrders || 0 }}</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-full bg-green-500"></span>
                <span class="text-gray-600">ชำระเงินแล้ว</span>
              </div>
              <span class="font-bold text-gray-800">{{ stats.paidOrders || 0 }}</span>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-full bg-purple-500"></span>
                <span class="text-gray-600">จัดส่งแล้ว</span>
              </div>
              <span class="font-bold text-gray-800">{{ stats.shippedOrders || 0 }}</span>
            </div>
          </div>
        </div>

        <!-- Best Sellers Widget -->
        <BestSellersWidget :bestSellers="stats.bestSellers" />

        <!-- Quick Actions -->
        <div class="bg-white p-4 sm:p-6 rounded-xl shadow">
          <h3 class="font-bold text-gray-700 mb-4">ทางลัด</h3>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <router-link to="/admin/orders" class="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <svg class="w-8 h-8 text-primary mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span class="text-sm text-gray-600">คำสั่งซื้อ</span>
            </router-link>
            <router-link to="/admin/products" class="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <svg class="w-8 h-8 text-primary mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <span class="text-sm text-gray-600">สินค้า</span>
            </router-link>
            <router-link to="/admin/categories" class="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <svg class="w-8 h-8 text-primary mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <span class="text-sm text-gray-600">หมวดหมู่</span>
            </router-link>
            <router-link to="/admin/reports" class="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <svg class="w-8 h-8 text-primary mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span class="text-sm text-gray-600">รายงาน</span>
            </router-link>
          </div>
        </div>
      </div>

      <!-- Recent Orders -->
      <div class="bg-white p-4 sm:p-6 rounded-xl shadow">
        <div class="flex items-center justify-between mb-4">
          <h3 class="font-bold text-gray-700">คำสั่งซื้อล่าสุด</h3>
          <router-link to="/admin/orders" class="text-primary hover:text-primary-dark text-sm">
            ดูทั้งหมด →
          </router-link>
        </div>
        
        <div v-if="stats.recentOrders && stats.recentOrders.length > 0" class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-gray-500 border-b">
                <th class="pb-3 font-medium">เลขที่</th>
                <th class="pb-3 font-medium">ลูกค้า</th>
                <th class="pb-3 font-medium text-right">ยอดรวม</th>
                <th class="pb-3 font-medium text-center">สถานะ</th>
                <th class="pb-3 font-medium">วันที่</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in stats.recentOrders" :key="order.id" class="border-b last:border-0 hover:bg-gray-50">
                <td class="py-3">
                  <router-link :to="`/admin/orders/${order.id}`" class="text-primary hover:underline font-medium">
                    {{ order.order_number }}
                  </router-link>
                </td>
                <td class="py-3 text-gray-700">{{ order.customer_name }}</td>
                <td class="py-3 text-right font-medium">฿{{ formatNumber(order.total_amount) }}</td>
                <td class="py-3 text-center">
                  <span :class="getStatusClass(order.status)" class="px-2 py-1 rounded-full text-xs font-medium">
                    {{ getStatusText(order.status) }}
                  </span>
                </td>
                <td class="py-3 text-gray-500">{{ order.created_at }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center py-8 text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p>ยังไม่มีคำสั่งซื้อ</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import api from '../../api';
import { useNotification } from '../../composables/useNotification';
import BestSellersWidget from '../../components/admin/BestSellersWidget.vue';
import { getOrderStatusLabel } from '../../utils/orderStatus';

const stats = ref({});
const loading = ref(true);
const { handleApiError } = useNotification();

onMounted(async () => {
  try {
    const response = await api.get('/admin/reports/dashboard');
    stats.value = response.data.data;
  } catch (error) {
    handleApiError(error);
  } finally {
    loading.value = false;
  }
});

const formatNumber = (num) => {
  return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(num);
};

const getStatusClass = (status) => {
  const classes = {
    'pending_payment': 'bg-yellow-100 text-yellow-800',
    'pending_verification': 'bg-orange-100 text-orange-800',
    'paid': 'bg-green-100 text-green-800',
    'processing': 'bg-blue-100 text-blue-800',
    'shipped': 'bg-purple-100 text-purple-800',
    'delivered': 'bg-emerald-100 text-emerald-800',
    'cancelled': 'bg-red-100 text-red-800'
  };
  return classes[status] || 'bg-gray-100 text-gray-800';
};

const getStatusText = (status) => getOrderStatusLabel(status);
</script>
